import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { LenderRepayments, LenderRepaymentsApi, RepaymentInstalmentRow } from '../tokens/lender-repayments.token';

/** {@code GET /api/v1/lender/repayments?range=} wire shape (B6 LenderRepaymentsAggregateDTO). */
interface RepaymentsAggregateWire {
  upcoming: RepaymentRowWire[];
  overdue: RepaymentRowWire[];
  upcomingTotal: number;
  overdueTotal: number;
}
interface RepaymentRowWire {
  loanReference: string;
  borrowerName: string;
  instalmentNumber: number;
  dueDate: string;
  totalDue: number;
  status: string;
}

/**
 * REST-only {@link LenderRepaymentsApi} (EM-699 / F6 — EM-711). Calls the existing B6
 * repayments aggregate; the day-count comes through as {@code range=<n>d}. Rides the Keycloak
 * bearer via {@code authInterceptor}.
 */
@Injectable()
export class LenderRepaymentsRestService implements LenderRepaymentsApi {
  private readonly http = inject(HttpClient);

  getRepayments(rangeDays: number): Observable<LenderRepayments> {
    const params = new HttpParams().set('range', `${rangeDays}d`);
    return this.http
      .get<RepaymentsAggregateWire>(API_PATHS.LENDER_REPAYMENTS, { params })
      .pipe(
        map(agg => ({
          upcoming: (agg.upcoming ?? []).map(toRow),
          overdue: (agg.overdue ?? []).map(toRow),
          upcomingTotal: agg.upcomingTotal ?? 0,
          overdueTotal: agg.overdueTotal ?? 0,
        })),
      );
  }
}

/** Days until due (negative for overdue), computed from today at local midnight. */
function daysUntil(dueDateIso: string): number {
  const due = new Date(dueDateIso + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

function toRow(r: RepaymentRowWire): RepaymentInstalmentRow {
  return {
    loanReference: r.loanReference,
    borrowerName: r.borrowerName,
    instalmentNumber: r.instalmentNumber,
    dueDate: r.dueDate,
    totalDue: r.totalDue,
    status: r.status,
    daysUntilDue: daysUntil(r.dueDate),
  };
}
